import React, { useState, useRef, useEffect } from 'react'
import classnames from 'classnames'
import useEditableName from '@Utils/UseEditableName'
import resizeImage from '@Utils/ResizeImage'
import AvatarUploader from '@Utils/AvatarUploader'
import { useSelector, useDispatch } from 'react-redux'
import Pen from '@Images/edit_customization_item.svg'

import {
  getCurrentAccountName,
  getCurrentAccountId,
  getSaving,
  getHasRenamedAccountSuccessfully,
  getUploadingAvatar,
  getCurrentAccountAvatarUrl,
} from '@SiteModules/Account/selectors'

import {
  renameAccount,
  changeAvatar,
  setUploadingAvatar,
} from '@SiteModules/Account/actions'

import './AccountInformation.scss'

const AccountInformation: React.FC = () => {
  const dispatch = useDispatch()
  const [showPen, setShowPen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const currentAccountName = useSelector(getCurrentAccountName)
  const currentAccountId = useSelector(getCurrentAccountId)
  const currentAccountAvatarUrl = useSelector(getCurrentAccountAvatarUrl)
  const saving = useSelector(getSaving)
  const hasRenamedAccountSuccessfully = useSelector(getHasRenamedAccountSuccessfully)
  const uploadingAvatar = useSelector(getUploadingAvatar)
  const lastDigitOfCurrentAccountId = currentAccountId % 10

  const handleRename = (name: string) => {
    dispatch(renameAccount({ accountId: currentAccountId, name }))
  }

  const {
    editing,
    editedName,
    inputWidth,
    nameContainerRef,
    inputRef,
    startEditing,
    changeName,
  } = useEditableName({
    name: currentAccountName,
    saving,
    hasRenamedSuccessfully: hasRenamedAccountSuccessfully,
    handleRename,
    inputClassName: 'AccountInformation__account-name-input'
  })

  useEffect(() => {
    if (uploadingAvatar && showPen) {
      hidePen()
    }
  }, [uploadingAvatar])

  const renderPen = () => {
    if (uploadingAvatar) { return }

    setShowPen(true)
  }

  const hidePen = () => {
    setShowPen(false)
  }

  const handleOnClickAvatar = () => {
    if (uploadingAvatar) { return }

    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    const resizedFile = await resizeImage(file, 150)

    const uploader = new AvatarUploader(resizedFile, {
      bucketName: `${window.Checksub.s3Bucket}/account/${currentAccountId}/avatars`,
      uploadAvatarCallback: ({ url }) => {
        dispatch(changeAvatar({ url, accountId: currentAccountId }))
      },
      uploadingAvatarCallback: () => {
        dispatch(setUploadingAvatar())
      }
    })

    uploader.call()
  }

  const renderHeader = () => {
    return (
      <div className="AccountInformation__header">
        <div className="AccountInformation__header-account-name">
          {currentAccountName}
        </div>
        <div className="AccountInformation__header-explanation">
          Manage all your account details
        </div>
      </div>
    )
  }

  const renderAccountName = () => {
    return (
      <React.Fragment>
        {editing ? (
          <input
            ref={inputRef}
            className="AccountInformation__account-name-input"
            style={{ width: inputWidth }}
            value={editedName}
            onChange={changeName}
            autoFocus
          />
        ) : (
          <div
            className="AccountInformation__account-name"
            onClick={startEditing}
            ref={nameContainerRef}
          >
            {currentAccountName}
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderAvatar = () => {
    const isPlaceholder = !currentAccountAvatarUrl

    return (
      <div
        className={classnames(
          'AccountInformation__avatar-base',
          isPlaceholder
            ? `AccountInformation__account-avatar-placeholder AccountInformation__account-avatar-placeholder-background-${lastDigitOfCurrentAccountId}`
            : 'AccountInformation__account-avatar',
          { 'is-disabled': uploadingAvatar }
        )}
        onMouseEnter={renderPen}
        onMouseLeave={hidePen}
        onClick={handleOnClickAvatar}
      >
        {isPlaceholder ? (
          showPen ? (
            <Pen />
          ) : (
            <span className="AccountInformation__account-avatar-initial">
              {currentAccountName.charAt(0)}
            </span>
          )
        ) : (
          <React.Fragment>
            <img
              src={currentAccountAvatarUrl}
              alt="Account avatar"
              className="AccountInformation__account-avatar-image"
            />
            {showPen && (
              <div className="AccountInformation__account-avatar-overlay">
                <Pen />
              </div>
            )}
          </React.Fragment>
        )}
      </div>
    )
  }

  return (
    <div className="AccountInformation">
      {renderHeader()}
      <div className="AccountInformation__account">
        {renderAvatar()}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        {renderAccountName()}
      </div>
    </div>
  )
}

export default AccountInformation
